import { useEffect } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchDepartements } from "../../reduxStore/DepartementSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { PARTAGE_TOUS } from "../../utils/partageFiltre";

/**
 * Filtre « Partage » des listes Article / Tiers (migration 0132) — choix multiple :
 *   - PARTAGE_TOUS → entités partagées avec tous les départements
 *   - un id        → entités partagées EXPLICITEMENT avec ce département
 * Aucune sélection = pas de filtre.
 */
interface SelectFiltrePartageProps {
  value: number[];
  onChange: (ids: number[]) => void;
}

const SelectFiltrePartage: React.FC<SelectFiltrePartageProps> = ({ value, onChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { departements, loading } = useSelector((state: RootState) => state.DepartementSlice);

  useEffect(() => {
    dispatch(fetchDepartements());
  }, [dispatch]);

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const v = event.target.value;
    onChange(typeof v === "string" ? v.split(",").map(Number) : v);
  };

  const renderValue = (selected: number[]) =>
    selected.length === 0
      ? "Tous"
      : selected
          .map((id) =>
            id === PARTAGE_TOUS ? "Partagés avec tous" : departements.find((d) => d.id === id)?.nom ?? id
          )
          .join(", ");

  return (
    <FormControl fullWidth size="small">
      <InputLabel id="select-filtre-partage-label" shrink>
        Partage
      </InputLabel>
      <Select<number[]>
        labelId="select-filtre-partage-label"
        multiple
        displayEmpty
        notched
        value={value}
        onChange={handleChange}
        label="Partage"
        disabled={loading}
        renderValue={renderValue}
      >
        <MenuItem value={PARTAGE_TOUS} divider>
          <Checkbox size="small" checked={value.includes(PARTAGE_TOUS)} />
          <ListItemText primary="Partagés avec tous" />
        </MenuItem>
        {departements.map((dept) => (
          <MenuItem key={dept.id} value={dept.id}>
            <Checkbox size="small" checked={dept.id !== undefined && value.includes(dept.id)} />
            <ListItemText primary={dept.nom} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SelectFiltrePartage;
